// @ts-nocheck
/**
 * <delivery-widget> — PDP delivery widget.
 *
 * A collapsible summary chip that expands into a panel with delivery-mode
 * toggles, ZIP / date / time / location fields, and apply / cancel actions.
 * Edits are STAGED in the panel and committed to the summary + cart on "apply".
 *
 * On apply the widget writes the SAME cart attributes the legacy
 * assets/delivery-selector.js wrote, so the server-side purchase gate in
 * blocks/buy-buttons.liquid (and snippets/product-delivery-availability.liquid)
 * keeps working unchanged:
 *   type, date ("DD MMMM YYYY"), zip, currentDeliveryDate ("Weekday|Month"),
 *   and for local: localDeliveryType, location, deliveryTime.
 * It then re-renders the product section (and header) via the Section Rendering
 * API so availability, the buy row, and the seasonal "Product available only in
 * X" message update. Time slots, ZIP -> location detection, and cutoff /
 * unavailable-day validation are ported from the legacy selector and use the
 * same window.locationData + global data-bethesda / data-georgetown zip lists.
 */

import { sectionRenderer } from '@theme/section-renderer';
import { getGeoZip, detectZip, canDetectZip, setGeoZip } from '@theme/geolocation';

const LOCATIONS = [
  { value: 'georgetown', label: 'georgetown' },
  { value: 'bethesda', label: 'bethesda' },
];

const WEEKDAYS = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MAX_DAYS_AHEAD = 30;

// Loader failsafe: longer than TIMEOUT_MS in assets/geolocation.js, shorter than
// --delivery-widget-detect-failsafe in assets/delivery-widget.css.
const DETECT_FAILSAFE_MS = 8000;

// One auto-commit per page view — a failed cart write must not retry in a loop
// across the reconnects the Section Rendering API triggers.
let autoCommitAttempted = false;

// Same, for the stale-committed refresh (#refreshStaleCommitted).
let staleRefreshAttempted = false;

// A dismissed delivery-error popup must not re-open on section re-renders;
// module-level because the widget element is replaced on re-render.
let popupDismissedFor = null;

// Set by an explicit apply, consumed by the mismatch check that follows it.
// A cross-page mismatch surfaced this way redirects the customer to the matching
// landing page; the same mismatch on page load only shows the message.
// Module-level because applyToCart re-renders the section between the two.
let applyRedirectPending = false;

// Cutoff / unavailable-day messages, ported from assets/delivery-selector.js.
const CUTOFF_ERRORS = {
  shipping: 'Shipping arrival is not available on this day. Please choose another day.',
  local: 'Pickup is not available on this day. Please choose another day.',
  delivery: 'Delivery is not available on this day. Please choose another day.',
};

const svgChevron = (w) =>
  `<svg width="${w}" height="${w}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>`;
const svgChevronLeft = (w) =>
  `<svg width="${w}" height="${w}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>`;

class DeliveryWidget extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();

    this.chip = this.querySelector('[data-dw-chip]');
    this.panel = this.querySelector('[data-dw-panel]');
    this.zipInput = this.querySelector('[data-dw-zip]');
    if (!this.chip || !this.panel) return;

    const showPickup = this.dataset.showPickup !== 'false';

    // Seed from the committed cart selection (server-rendered) when present,
    // otherwise from the block defaults.
    const hasCommitted = (this.dataset.committedType || '').trim() !== '';
    let mode = hasCommitted
      ? (this.dataset.committedType === 'nationwide' ? 'nationwide' : 'local')
      : (this.dataset.defaultMode === 'nationwide' ? 'nationwide' : 'local');
    const submode = hasCommitted ? (this.dataset.committedSubmode || 'delivery') : 'delivery';
    let zip = hasCommitted ? (this.dataset.committedZip || '').trim() : '';
    const time = hasCommitted ? (this.dataset.committedTime || '') : '';

    this.bethesdaZips = this.#zipList('data-bethesda');
    this.georgetownZips = this.#zipList('data-georgetown');
    this.hasDayjs = Boolean(this.#dayjs());
    this.userEditedZip = false;

    // Geolocation config (global theme settings, passed through the block).
    this.geoConfig = { enabled: this.dataset.geoEnable === 'true', apiKey: this.dataset.geoApiKey || '' };

    // Not committed: a cached geo ZIP pre-fills the field, then the ZIP drives the
    // mode. While detection is still pending the block default is held back —
    // #seedFromGeo applies the detected ZIP, or the default only once detection
    // has failed. A committed selection always wins over the detected value.
    this.geoPending = false;
    this.geoAbandoned = false;
    if (!hasCommitted) {
      const geoZip = getGeoZip();
      if (geoZip) {
        zip = geoZip;
      } else if (canDetectZip(this.geoConfig)) {
        this.geoPending = true;
      } else {
        zip = (this.dataset.defaultZip || '').trim();
      }
      if (!this.geoPending) mode = this.#modeForZip(zip, mode);
    }
    this.#setDetecting(this.geoPending);

    // Local delivery derives its location from the ZIP — seed it on load so time
    // slots work on first open, not only after the ZIP is re-typed.
    let location = hasCommitted ? (this.dataset.committedLocation || '') : '';
    if (mode === 'local' && submode === 'delivery' && !location) {
      location = this.#detectLocation(zip) || '';
    }

    // A committed selection or a configured default ZIP pre-fills the date; a
    // blank default ZIP (setting cleared) starts with no date so the field is empty.
    // A past committed date is clamped for display only — the cart still holds
    // the stale attributes, so it is flagged for #refreshStaleCommitted.
    let date = null;
    this.committedDateStale = false;
    if (hasCommitted) {
      date = this.#defaultDate(mode);
      if (this.dataset.committedDateIso) {
        const d = this.#dayjs()?.(this.dataset.committedDateIso);
        if (d && d.isValid && d.isValid()) {
          const min = this.#minDate(mode);
          if (min && d.isBefore(min, 'day')) {
            this.committedDateStale = true;
            date = min;
          } else {
            date = d;
          }
        }
      } else if (this.hasDayjs) {
        // Committed type without a parseable date — junk cart attribute.
        this.committedDateStale = true;
      }
    } else if (zip !== '') {
      date = this.#defaultDate(mode);
    }

    // Committed = what the chip summary reflects. Staged = in-panel edits.
    this.committed = { mode, submode, zip, location, date, time, showPickup };
    this.staged = { ...this.committed };

    this.activePopover = null;
    this.activeTrigger = null;
    this.calendarMonth = null;

    // Delivery error popup (landing pages only) — portaled to <body> like the
    // calendar/dropdown popovers so panel transforms can't trap its overlay.
    this.errorPopup = this.querySelector('.js-delivery-error-popup');
    if (this.errorPopup) {
      this.#bindPopupEvents();
      document.body.appendChild(this.errorPopup);
    }

    this.#syncState();
    this.#restoreFromState();
    this.#renderSummary();
    this.#bindEvents();
    if (this.geoPending) this.#seedFromGeo(); else this.#autoCommit();
    this.#refreshStaleCommitted();
    this.#watchAutoSelection();
    this.#toggleGatedSections();
  }

  disconnectedCallback() {
    this.#cancelMismatchRedirect();
    this.#closePopover();
    this.errorPopup?.remove();
    this.errorPopup = null;
    clearInterval(this.staleTimer);
    clearTimeout(this.detectTimer);
    this.abortController?.abort();
  }

  /* ---------------------------------------------------------------- events */

  #bindEvents() {
    const { signal } = this.abortController;

    this.chip.addEventListener('click', () => this.#togglePanel(), { signal });

    this.querySelectorAll('[data-dw-toggle]').forEach((track) => {
      track.querySelectorAll('[data-dw-option]').forEach((opt) => {
        opt.addEventListener('click', () => this.#selectToggle(track, opt.dataset.dwOption), { signal });
      });
    });

    this.zipInput?.addEventListener('input', (e) => {
      const clean = e.target.value.replace(/\D/g, '').slice(0, 5);
      if (clean !== e.target.value) e.target.value = clean;
      this.staged.zip = clean;
      this.userEditedZip = true;
      // The customer's own ZIP supersedes detection — drop the loader with it.
      if (this.geoPending) this.#setDetecting(false);
      // ZIP drives the mode (empty/non-local -> nationwide, local -> local).
      this.#deriveModeFromZip();
      if (this.staged.mode === 'local' && this.staged.submode === 'delivery') {
        this.#syncLocationFromZip();
      }
      this.#hideError();
    }, { signal });

    // Open on a click anywhere in the FIELD (icon, chevron, padding — not just the
    // inner value button). aria wiring stays on the button, which is passed as the
    // popover trigger.
    this.querySelector('[data-dw-field="date"]')?.addEventListener('click', () => {
      const trigger = this.querySelector('[data-dw-date-trigger]');
      if (trigger) this.#openCalendar(trigger);
    }, { signal });
    this.querySelector('[data-dw-field="time"]')?.addEventListener('click', () => {
      const trigger = this.querySelector('[data-dw-time-trigger]');
      if (trigger) this.#openTimeDropdown(trigger);
    }, { signal });
    this.querySelector('[data-dw-field="location"]')?.addEventListener('click', () => {
      const trigger = this.querySelector('[data-dw-location-trigger]');
      if (trigger) this.#openLocationDropdown(trigger);
    }, { signal });

    this.querySelector('[data-dw-apply]')?.addEventListener('click', () => this.#apply(), { signal });
    this.querySelector('[data-dw-cancel]')?.addEventListener('click', () => this.#cancel(), { signal });

    document.addEventListener('pointerdown', (e) => this.#onDocPointerDown(e), { signal });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.#onEscape(); }, { signal });
    // Capture phase: must run (and stop the event) before the legacy handler in
    // custom.js, which targets the removed <delivery-selector> and otherwise
    // redirects the customer to a PLP page.
    document.addEventListener('click', (e) => this.#onChooseNextDate(e), { capture: true, signal });
  }

  #onDocPointerDown(e) {
    // Outside clicks close an open popover (calendar / dropdown) only. The panel
    // itself is closed exclusively via the chip, cancel, or apply — never an
    // outside click. The whole field is the trigger surface, so a click anywhere
    // in it counts as "inside" (a padding click must not close-then-reopen).
    if (this.activePopover) {
      const anchor = this.activeTrigger?.closest('.delivery-widget__field') || this.activeTrigger;
      if (!this.activePopover.contains(e.target) && !anchor?.contains(e.target)) {
        this.#closePopover();
      }
    }

    // When the open panel floats over the page (mobile always; desktop in
    // overlay mode) a click outside the widget collapses it — same as Escape.
    // An in-flow panel (PDP desktop) pushes content down instead of covering
    // it, so outside clicks are left alone there.
    if (!this.classList.contains('is-open')) return;
    // Clicks in the portaled error popup are not "outside" — don't cancel.
    if (this.contains(e.target) || this.activePopover?.contains(e.target) || this.errorPopup?.contains(e.target)) return;
    if (!this.panel || getComputedStyle(this.panel).position !== 'absolute') return;
    this.#cancel();
  }

  #onEscape() {
    // A visible error popup swallows Escape — close it, keep the panel.
    if (this.errorPopup && !this.errorPopup.hidden) { this.#hideErrorPopup(true); return; }
    if (this.activePopover) { this.#closePopover(); this.activeTrigger?.focus(); return; }
    if (this.classList.contains('is-open')) this.#cancel();
  }

  /* ------------------------------------------------------------ panel open */

  #togglePanel(force) {
    const open = force ?? !this.classList.contains('is-open');
    this.classList.toggle('is-open', open);
    this.chip.setAttribute('aria-expanded', String(open));
    if (!open) this.#closePopover();
  }

  /* -------------------------------------------------------------- toggles */

  #selectToggle(track, value) {
    const key = track.dataset.dwToggle; // "mode" | "submode"
    if (key === 'mode') {
      // Mode is ZIP-driven: clicking a mode the current ZIP doesn't support
      // pulses the ZIP field to prompt a correction instead of switching.
      if (value !== this.staged.mode) this.#pulseZip();
      return;
    }
    if (this.staged[key] === value) return;
    this.staged[key] = value;
    this.#applyToggleUI('submode', value);

    // Pickup is always fulfilled locally regardless of the ZIP; leaving pickup
    // re-derives the mode from the ZIP (local -> delivery, otherwise shipping).
    const mode = value === 'pickup' ? 'local' : this.#modeForZip(this.staged.zip, this.staged.mode);
    if (mode !== this.staged.mode) {
      this.#setMode(mode);
      return;
    }

    // Local delivery derives the location from the ZIP; pickup picks it manually.
    if (this.staged.mode === 'local' && this.staged.submode === 'delivery') {
      this.#syncLocationFromZip();
    }

    // Slots depend on sub-mode / location — keep an already-picked time when
    // the new list still offers it, otherwise force a fresh pick.
    this.#preserveOrResetTime();
    this.#closePopover();
    this.#hideError();
    this.#syncState();
  }

  // Mode follows the ZIP: empty / 5-digit non-local -> nationwide, local -> local;
  // a partial ZIP keeps the current mode so the toggle doesn't flicker while typing.
  #modeForZip(zip, fallback) {
    const z = (zip || '').trim();
    if (z === '') return 'nationwide';
    if (this.#detectLocation(z)) return 'local';
    if (z.length === 5) return 'nationwide';
    return fallback;
  }

  #deriveModeFromZip() {
    if (this.staged.submode === 'pickup') return; // pickup is mode-locked to local
    const mode = this.#modeForZip(this.staged.zip, this.staged.mode);
    if (mode !== this.staged.mode) this.#setMode(mode);
  }

  #setMode(mode) {
    this.staged.mode = mode;
    if (mode === 'nationwide') this.staged.submode = 'delivery'; // pickup is always local

    // date floor: local = today, nationwide = tomorrow
    if (this.staged.date) {
      const min = this.#minDate(mode);
      if (min && this.staged.date.isBefore(min, 'day')) this.staged.date = min;
      this.#renderDateLabel();
    }

    this.#applyToggleUI('mode', mode);
    this.#applyToggleUI('submode', this.staged.submode);
    this.#preserveOrResetTime();
    this.#closePopover();
    this.#hideError();
    this.#syncState();
  }

  #applyToggleUI(key, value) {
    const track = this.querySelector(`[data-dw-toggle="${key}"]`);
    if (!track) return;
    const options = [...track.querySelectorAll('[data-dw-option]')];
    options.forEach((o) => o.setAttribute('aria-pressed', String(o.dataset.dwOption === value)));
    const idx = options.findIndex((o) => o.dataset.dwOption === value);
    track.querySelector('[data-dw-thumb]')?.classList.toggle('delivery-widget__toggle-thumb--right', idx === 1);
  }

  // Pulse the ZIP field to signal it must be corrected to switch modes.
  #pulseZip() {
    const field = this.querySelector('[data-dw-field="zip"]');
    if (!field) return;
    field.classList.remove('delivery-widget__field--attention');
    void field.offsetWidth; // restart the animation on repeated clicks
    field.classList.add('delivery-widget__field--attention');
    field.addEventListener('animationend', () => field.classList.remove('delivery-widget__field--attention'), { once: true });
  }

  /* ------------------------------------------------------------ calendar */

  #openCalendar(trigger) {
    if (!this.hasDayjs) return; // dayjs unavailable — keep server-rendered date
    if (this.activeTrigger === trigger) { this.#closePopover(); return; }
    this.#closePopover();

    this.calendarMonth = (this.staged.date || this.#today()).startOf('month');
    const pop = document.createElement('div');
    pop.className = 'dw-calendar';
    pop.innerHTML = '<div class="dw-calendar__card"><div class="dw-calendar__body"></div></div>';
    document.body.appendChild(pop);

    this.#mountPopover(pop, trigger, { width: 320, align: 'right' });
    this.#renderCalendar();
    // re-measure after the month grid renders so the flip-above check uses the real height
    this._reposition?.();
  }

  #renderCalendar() {
    const body = this.activePopover?.querySelector('.dw-calendar__body');
    if (!body) return;

    const month = this.calendarMonth;
    const min = this.#minDate(this.staged.mode);
    const max = this.#today().add(MAX_DAYS_AHEAD, 'day');
    const firstWeekday = (month.day() + 6) % 7; // 0 = Monday
    const daysInMonth = month.daysInMonth();

    const prevDisabled = month.isSame(min, 'month') || month.isBefore(min, 'month');
    const nextDisabled = month.isSame(max, 'month') || month.isAfter(max, 'month');

    let cells = '';
    for (let i = 0; i < firstWeekday; i += 1) cells += '<div class="dw-calendar__day dw-calendar__day--empty" aria-hidden="true"></div>';
    for (let d = 1; d <= daysInMonth; d += 1) {
      const date = month.date(d);
      const disabled = date.isBefore(min, 'day') || date.isAfter(max, 'day');
      const selected = this.staged.date ? date.isSame(this.staged.date, 'day') : false;
      cells += `<button type="button" class="dw-calendar__day${selected ? ' is-selected' : ''}" data-day="${d}"${disabled ? ' disabled' : ''}><span>${d}</span></button>`;
    }

    body.innerHTML = `
      <div class="dw-calendar__head">
        <button type="button" class="dw-calendar__nav" data-cal-prev aria-label="Previous month"${prevDisabled ? ' disabled' : ''}>${svgChevronLeft(20)}</button>
        <p class="dw-calendar__title">${MONTHS[month.month()]} ${month.year()}</p>
        <button type="button" class="dw-calendar__nav" data-cal-next aria-label="Next month"${nextDisabled ? ' disabled' : ''}>${svgChevron(20)}</button>
      </div>
      <div class="dw-calendar__grid">${WEEKDAYS.map((w) => `<div class="dw-calendar__weekday">${w}</div>`).join('')}</div>
      <div class="dw-calendar__grid">${cells}</div>`;

    body.querySelector('[data-cal-prev]')?.addEventListener('click', () => { this.calendarMonth = this.calendarMonth.subtract(1, 'month'); this.#renderCalendar(); });
    body.querySelector('[data-cal-next]')?.addEventListener('click', () => { this.calendarMonth = this.calendarMonth.add(1, 'month'); this.#renderCalendar(); });
    body.querySelectorAll('.dw-calendar__day[data-day]').forEach((btn) => {
      if (btn.disabled) return;
      btn.addEventListener('click', () => {
        this.staged.date = this.calendarMonth.date(Number(btn.dataset.day));
        this.#renderDateLabel();
        this.#resetTime(); // slots differ per day
        this.#hideError();
        this.#closePopover();
      });
    });
  }

  /* ---------------------------------------------------------- dropdowns */

  #openTimeDropdown(trigger) {
    const slots = this.#timeSlots();
    if (slots.length === 0) {
      // No schedule / date / location yet, or the day is fully unavailable.
      const message = (!this.staged.date || !this.staged.location)
        ? 'select a date and location first'
        : 'no times available for this day';
      this.#openDropdown(trigger, [{ value: '', label: message, lower: true, disabled: true }], this.staged.time, () => {});
      return;
    }
    const options = slots.map((t) => ({ value: t, label: t, lower: true }));
    this.#openDropdown(trigger, options, this.staged.time, (v) => {
      this.staged.time = v;
      this.querySelector('[data-dw-time-label]').textContent = v;
      this.#hideError();
    });
  }

  #openLocationDropdown(trigger) {
    const options = LOCATIONS.map((l) => ({ ...l, lower: true }));
    this.#openDropdown(trigger, options, this.staged.location, (v) => {
      this.staged.location = v;
      this.#renderLocationLabel();
      this.#resetTime(); // slots depend on location
      this.#hideError();
    });
  }

  #openDropdown(trigger, options, selected, onPick) {
    if (this.activeTrigger === trigger) { this.#closePopover(); return; }
    this.#closePopover();

    const pop = document.createElement('div');
    pop.className = 'dw-dropdown';
    pop.innerHTML = `<div class="dw-dropdown__card" role="listbox">${options
      .map((o) => `<button type="button" role="option" class="dw-dropdown__option${o.value === selected ? ' is-selected' : ''}"${o.disabled ? ' disabled aria-disabled="true"' : ''} data-value="${o.value}">${o.label}</button>`)
      .join('')}</div>`;
    document.body.appendChild(pop);

    // full width of the field (matches the mockup: time/location dropdowns span the whole select)
    this.#mountPopover(pop, trigger);
    pop.querySelectorAll('.dw-dropdown__option').forEach((btn) => {
      if (btn.disabled) return;
      btn.addEventListener('click', () => { onPick(btn.dataset.value); this.#closePopover(); });
    });
  }

  /* --------------------------------------------------------- popover base */

  /**
   * Position a portaled popover relative to its FIELD (not the inner value
   * button, which is shorter than the field and made popovers overlap it).
   * opts.width  — fixed width in px (calendar); omit to match the field width.
   * opts.align  — 'right' right-aligns to the field's right edge (calendar on
   *               desktop); default 'left' aligns to the field's left edge.
   * Opens below the field; flips above only when there is no room below.
   */
  #mountPopover(pop, trigger, opts = {}) {
    this.activePopover = pop;
    this.activeTrigger = trigger;
    const field = trigger.closest('.delivery-widget__field') || trigger;
    field.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');

    const gap = 4;
    const margin = 8;
    const align = opts.align === 'right' ? 'right' : 'left';
    const fixedWidth = opts.width || null;

    const reposition = () => {
      const r = field.getBoundingClientRect();
      const w = fixedWidth || r.width;
      pop.style.width = `${w}px`;

      let left = align === 'right' ? r.right - w : r.left;
      left = Math.min(left, window.innerWidth - w - margin);
      left = Math.max(margin, left);
      pop.style.left = `${left}px`;

      const popH = pop.offsetHeight;
      let top = r.bottom + gap;
      if (top + popH > window.innerHeight - margin && r.top - gap - popH > margin) {
        top = r.top - gap - popH;
      }
      pop.style.top = `${top}px`;
    };
    reposition();
    this._reposition = reposition;
    window.addEventListener('scroll', reposition, { passive: true, capture: true });
    window.addEventListener('resize', reposition, { passive: true });
  }

  #closePopover() {
    if (this._reposition) {
      window.removeEventListener('scroll', this._reposition, { capture: true });
      window.removeEventListener('resize', this._reposition);
      this._reposition = null;
    }
    if (this.activeTrigger) {
      this.activeTrigger.setAttribute('aria-expanded', 'false');
      this.activeTrigger.closest('.delivery-widget__field')?.classList.remove('is-open');
    }
    this.activePopover?.remove();
    this.activePopover = null;
    this.activeTrigger = null;
  }

  /* --------------------------------------------------------- apply/cancel */

  #apply() {
    applyRedirectPending = true;
    const error = this.#validate();
    if (error) { this.#surfaceError(error); return; }
    this.#hideError();
    this.#hideErrorPopup(false);

    // An explicit choice ends the auto-managed watch (see #refreshAutoSelection).
    try { sessionStorage.removeItem('mtn:dw_auto_selection'); } catch (e) { /* no-op */ }

    this.committed = { ...this.staged };
    if (this.committed.zip) setGeoZip(this.committed.zip);
    this.#renderSummary();
    this.#togglePanel(false);
    this.applyToCart();
  }

  #cancel() {
    this.staged = { ...this.committed };
    this.#restoreFromState();
    this.#hideError();
    this.#togglePanel(false);
    this.#restorePageMismatch();
  }

  // The cross-page mismatch is page state, not a staged edit — after #cancel
  // wipes the error, put the inline text back if the mismatch still exists.
  #restorePageMismatch() {
    if (!document.querySelector('.image-text-grid__inactive-message')) return;
    const pageMode = this.dataset.defaultMode === 'nationwide' ? 'nationwide' : 'local';
    const committedMode = (this.dataset.committedType || '').trim();
    if (committedMode && committedMode !== pageMode) this.#renderMismatchInline(pageMode);
  }

  /**
   * Commit the selection to the cart, then re-render the product section (and
   * header) so availability, the buy row, and the seasonal message update.
   * Writes the same attribute shape as the legacy delivery-selector.
   */
  async applyToCart() {
    const attributes = this.#buildAttributes();
    this.classList.add('is-applying');

    const url = `${(window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update'}.js`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error(`cart update failed: ${response.status}`);
      const result = await response.json();
      if (!result || !result.token) return;

      this.#persist(attributes);

      // Sections that replaced the legacy selector hero (flavors pages) render
      // type-dependent markup server-side and keep a hidden mirror of the old
      // selector inputs for their scripts. A committed TYPE change needs a full
      // reload there; same-type edits sync the mirror in place (flavors.js
      // caches those elements once, so they must be updated, not re-rendered).
      const legacyState = document.querySelector('[data-dw-legacy-state]');
      if (legacyState) {
        const previousType = (this.dataset.committedType || '').trim();
        if (previousType !== attributes.type) {
          window.location.reload();
          return;
        }
        this.#syncLegacyState(legacyState, attributes);
      }

      document.dispatchEvent(new CustomEvent('delivery-changed'));
      this.#refreshFacets();

      await this.#rerender();

      // Section Rendering API morphs in place: if this element survived the
      // morph, resync it from the freshly-rendered committed attributes; if the
      // product became unavailable it was replaced by the not-available message
      // and this element is no longer connected.
      if (this.isConnected) {
        this.disconnectedCallback();
        this.connectedCallback();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('delivery-widget: cart update failed', error);
    } finally {
      this.classList.remove('is-applying');
    }
  }

  // On the PLP the product grid is driven by the facets form — re-run it so
  // availability filtering reflects the new delivery selection (port of the
  // legacy delivery-selector triggerFilters). No-op on pages without facets.
  #refreshFacets() {
    const facets = document.querySelector('facets-form-component');
    if (facets && typeof facets.updateFilters === 'function') facets.updateFilters();
  }

  /**
   * Landing pages (local orders / ship nationwide) gate their content on the
   * delivery selection: buttons in the sections below stay disabled and the
   * grid shows its "enter your details above to continue" message until a
   * valid selection is committed. Port of the legacy delivery-selector
   * toggleSectionsVisibility(), scoped to pages that render that message —
   * a no-op everywhere else (PLP / PDP use server-side gates instead).
   * Runs on every (re)connect, which applyToCart triggers after each apply.
   */
  #toggleGatedSections() {
    const messages = document.querySelectorAll('.image-text-grid__inactive-message');
    if (!messages.length) return;

    // The committed selection must match the PAGE's delivery type: on the
    // local-orders page a nationwide selection keeps the content gated, and
    // vice versa on ship-nationwide (legacy parity — the old selector
    // validated the ZIP against its section default_type the same way).
    // The block's default_mode carries the page type, exactly like the old
    // section's default_type did.
    const pageMode = this.dataset.defaultMode === 'nationwide' ? 'nationwide' : 'local';
    const committedMode = (this.dataset.committedType || '').trim();
    // Without dayjs the committed date can't be re-validated — trust the
    // committed cart attributes alone rather than gating the page forever.
    const active = committedMode === pageMode && (!this.hasDayjs || !this.#validate());

    const skipButton = (button) =>
      button.classList.contains('js-add-bundle') ||
      button.classList.contains('button--js-disabled') ||
      button.classList.contains('product-card__button-sold-out') ||
      button.classList.contains('show-more__btn') ||
      button.classList.contains('clear-filter') ||
      button.classList.contains('js-details') ||
      button.name === 'change-view';

    for (const section of document.querySelectorAll('.shopify-section')) {
      if (
        section.contains(this) ||
        section.classList.contains('header-section') ||
        section.classList.contains('shopify-section-group-footer-group')
      ) continue;

      for (const button of section.querySelectorAll('.button, button, [role="button"]')) {
        if (skipButton(button)) continue;
        button.classList.toggle('button-disabled', !active);
        button.disabled = !active;
        button.style.pointerEvents = active ? '' : 'none';
        // Mirror the server-rendered initial gate (image-text-grid renders
        // blocked links with tabindex/aria-disabled to avoid a flash of
        // enabled buttons) — keep those attributes in sync both ways.
        if (active) {
          button.removeAttribute('aria-disabled');
          if (button.getAttribute('tabindex') === '-1') button.removeAttribute('tabindex');
        } else {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('tabindex', '-1');
        }
      }
    }

    for (const message of messages) message.classList.toggle('is-active', !active);

    // A committed selection of the WRONG type for this page also opens the
    // panel with the legacy cross-page error ("Please choose nationwide
    // shipping / local pickup instead"), so the customer sees why the page
    // is locked and where to go — parity with the old selector's
    // delivery-type error, which was always visible in its inline form.
    // Consumed here even without a mismatch: an apply that resolved the page
    // type must not leave the flag armed for a later page-load mismatch.
    const fromApply = applyRedirectPending;
    applyRedirectPending = false;

    if (committedMode && committedMode !== pageMode) {
      this.#showPageMismatch(pageMode);
      if (fromApply) this.#scheduleMismatchRedirect(pageMode);
    }
  }

  // Cross-page mismatch: the inline message always shows; the popup (when
  // enabled and rendered — landing pages only) opens on top of it.
  #showPageMismatch(pageMode) {
    if (this.dataset.errorDisplay !== 'inline' && this.errorPopup) {
      this.#showErrorPopup(pageMode, (this.dataset.committedZip || '').trim(), false);
    }
    this.#renderMismatchInline(pageMode);
    this.#togglePanel(true);
  }

  #renderMismatchInline(pageMode) {
    const el = this.querySelector('[data-dw-error]');
    if (!el) return;
    el.innerHTML = pageMode === 'local'
      ? 'Local delivery isn’t available for this ZIP code.<br>Please choose <a class="delivery-widget__error-link" href="/pages/ship-nationwide">nationwide shipping</a> or local pickup instead.'
      : 'Shipping is not available for the selected ZIP code.<br>Please choose <a class="delivery-widget__error-link" href="/pages/local-orders">local</a> pickup or delivery instead.';
    el.hidden = false;
  }

  async #rerender() {
    const jobs = [];
    const seen = new Set();
    const push = (id) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      jobs.push(sectionRenderer.renderSection(id.replace('shopify-section-', ''), { cache: false }));
    };

    push(this.closest('.shopify-section')?.id);
    push(document.querySelector('header')?.id);

    // Product-card availability ("ways to buy" vs add-to-cart) is server-
    // rendered from the cart attributes — refresh every section that shows
    // cards (home page product lists, etc.). PLP grids are refreshed through
    // the facets form instead (#refreshFacets), so skip them here.
    if (!document.querySelector('facets-form-component')) {
      for (const card of document.querySelectorAll('.product-card__detail-wrap')) {
        push(card.closest('.shopify-section')?.id);
      }
    }

    await Promise.all(jobs);
  }

  #buildAttributes() {
    const c = this.committed;
    const attributes = {
      type: c.mode, // 'local' | 'nationwide'
      date: this.#storageDate(c.date),
      zip: c.zip || '',
      currentDeliveryDate: this.#currentDeliveryDate(c.date),
    };
    if (c.mode === 'local') {
      attributes.localDeliveryType = c.submode; // 'delivery' | 'pickup'
      attributes.location = c.location || '';
      attributes.deliveryTime = c.time || '';
    } else {
      // Clear the local-only attributes so a prior local selection cannot leak
      // into a nationwide order.
      attributes.localDeliveryType = '';
      attributes.location = '';
      attributes.deliveryTime = '';
    }
    return attributes;
  }

  /**
   * Persist the selection for cross-page parity with the legacy selector.
   * NOTE: these keys are intentionally un-prefixed (not `mtn:`) because
   * assets/product-card-control.js and the legacy flow read these exact keys.
   */
  #persist(attributes) {
    try {
      localStorage.setItem('delivery-ship-type', attributes.type);
      localStorage.setItem('delivery-date', attributes.date);
      localStorage.setItem('delivery-zip', attributes.zip || '');
      if (attributes.type === 'local') {
        localStorage.setItem('delivery-type', attributes.localDeliveryType);
        localStorage.setItem('delivery-location', attributes.location || '');
        localStorage.setItem('delivery-time', attributes.deliveryTime || '');
      }
    } catch (e) {
      /* storage unavailable — non-fatal */
    }
  }

  /**
   * Push the committed attributes into the hidden legacy-state mirror rendered
   * by sections that dropped the old selector hero (see
   * snippets/delivery-widget-legacy-state.liquid). Values keep the legacy
   * formats: date "DD MMMM YYYY", location "georgetown" / "bethesda", time
   * "11:00AM - 12:00PM", delivery-type radios "delivery" / "pickup".
   */
  #syncLegacyState(root, attributes) {
    const set = (selector, value) => {
      const el = root.querySelector(selector);
      if (el) el.value = value || '';
    };
    set('.js-delivery-date', attributes.date);
    set('.js-delivery-zip', attributes.zip);
    set('.js-location', attributes.location);
    set('.js-time', attributes.deliveryTime);
    for (const radio of root.querySelectorAll('[name="delivery-type"]')) {
      radio.checked = radio.value === attributes.localDeliveryType;
    }
  }

  /* -------------------------------------------------------------- validate */

  #validate() {
    const s = this.staged;
    // Which delivery-error-popup copy a mismatch maps to ('local' |
    // 'nationwide'), or null when the error isn't a ZIP/page-type mismatch.
    this.zipMismatch = null;
    if (!s.date) return 'Please choose a delivery date.';

    if (s.mode === 'nationwide') {
      const zip = (s.zip || '').trim();
      if (!/^\d{5}$/.test(zip)) return 'Please enter a valid 5-digit ZIP code.';
      if (this.#zipType(zip) === 'local') {
        this.zipMismatch = 'nationwide';
        return 'This ZIP is local. Choose local delivery or pickup instead.';
      }
    } else {
      // local
      if (s.submode === 'delivery') {
        const zip = (s.zip || '').trim();
        if (!zip) return 'Please enter your ZIP code.';
        const location = this.#detectLocation(zip);
        if (!location) {
          this.zipMismatch = 'local';
          return 'Local delivery isn’t available for this ZIP. Choose nationwide shipping or pickup.';
        }
        s.location = location;
      } else if (!s.location) {
        return 'Please choose a pickup location.';
      }
      if (!s.time) return 'Please choose a delivery time.';
    }

    return this.#checkCutoff();
  }

  #showError(message) {
    const el = this.querySelector('[data-dw-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  #hideError() {
    this.#cancelMismatchRedirect();
    const el = this.querySelector('[data-dw-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  /* ---------------------------------------------------------- error popup */

  // Inline error always shows; a ZIP mismatch also opens the popup when
  // enabled (forced: an explicit apply re-shows it even after a dismissal).
  #surfaceError(message) {
    if (this.zipMismatch && this.dataset.errorDisplay !== 'inline' && this.errorPopup) {
      this.#showErrorPopup(this.zipMismatch, (this.staged.zip || '').trim(), true);
    }
    this.#showError(message);
    if (this.zipMismatch) {
      applyRedirectPending = false;
      this.#scheduleMismatchRedirect(this.zipMismatch);
    }
  }

  #scheduleMismatchRedirect(pageMode) {
    if (!document.querySelector('.image-text-grid__inactive-message')) return;
    clearTimeout(this.redirectTimer);
    this.redirectTimer = setTimeout(() => {
      window.location.href = pageMode === 'local' ? '/pages/ship-nationwide' : '/pages/local-orders';
    }, 3000);
  }

  #cancelMismatchRedirect() {
    clearTimeout(this.redirectTimer);
    this.redirectTimer = null;
  }

  #popupCopy(kind) {
    const d = this.errorPopup.dataset;
    return kind === 'nationwide'
      ? { title: d.nationwideTitle, text: d.nationwideText, label: d.nationwideLabel, href: d.nationwideHref }
      : { title: d.localTitle, text: d.localText, label: d.localLabel, href: d.localHref };
  }

  #showErrorPopup(kind, zip, force) {
    const popup = this.errorPopup;
    if (!popup) return;

    // Don't re-open an auto-triggered popup the user already dismissed for
    // this mismatch (page-load checks re-run on every section re-render).
    const signature = `${kind}|${zip}`;
    if (!force && popupDismissedFor === signature) return;
    this.popupSignature = signature;

    const copy = this.#popupCopy(kind);
    const fill = (template) => (template || '').replace(/\{\{\s*ZIP\s*\}\}/gi, zip);
    popup.querySelector('.js-delivery-popup-title').textContent = fill(copy.title);
    popup.querySelector('.js-delivery-popup-message').textContent = fill(copy.text);
    const action = popup.querySelector('.js-delivery-popup-action');
    action.textContent = copy.label || '';
    action.dataset.href = copy.href || '';

    const wasHidden = popup.hidden;
    popup.hidden = false;
    if (wasHidden) {
      this.lastFocusedBeforePopup = document.activeElement;
      action.focus();
    }
  }

  #hideErrorPopup(userDismissed) {
    const popup = this.errorPopup;
    if (!popup || popup.hidden) return;
    if (userDismissed) popupDismissedFor = this.popupSignature;
    popup.hidden = true;
    if (userDismissed && typeof this.lastFocusedBeforePopup?.focus === 'function') {
      this.lastFocusedBeforePopup.focus();
    }
    this.lastFocusedBeforePopup = null;
  }

  #bindPopupEvents() {
    const popup = this.errorPopup;
    const { signal } = this.abortController;

    popup.querySelectorAll('.js-delivery-popup-close').forEach((btn) =>
      btn.addEventListener('click', () => this.#hideErrorPopup(true), { signal })
    );

    popup.querySelector('.js-delivery-popup-action')?.addEventListener('click', (e) => {
      const href = e.currentTarget.dataset.href;
      if (href) window.location.href = href;
    }, { signal });

    popup.querySelector('.popup__background')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.#hideErrorPopup(true);
    }, { signal });

    popup.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { this.#hideErrorPopup(true); return; }
      if (e.key !== 'Tab') return;

      const focusables = popup.querySelectorAll('button:not([disabled])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }, { signal });
  }

  /**
   * Cutoff + unavailable-day validation, ported from delivery-selector.js.
   * Returns an error string, or null when the selection is orderable.
   */
  #checkCutoff() {
    const dayjs = this.#dayjs();
    const data = window.locationData;
    const s = this.staged;
    if (!dayjs || !data) return null; // graceful — schedule data absent

    const key = s.mode === 'nationwide' ? 'nationwide' : s.location;
    const location = data[key];
    if (!location) return null;

    const selected = s.date;
    const selectedFmt = selected.format('YYYY-MM-DD');
    const dayOfWeek = selected.format('dddd');
    const todayEST = this.#nowEST();
    const todayFmt = todayEST.format('YYYY-MM-DD');
    const todayDayOfWeek = todayEST.format('dddd');
    const isToday = selectedFmt === todayFmt;
    const isTomorrow = selectedFmt === todayEST.add(1, 'day').format('YYYY-MM-DD');

    const msg = s.mode === 'nationwide'
      ? CUTOFF_ERRORS.shipping
      : (s.submode === 'pickup' ? CUTOFF_ERRORS.local : CUTOFF_ERRORS.delivery);

    const unavailableDates = location.unavailable_dates || [];
    const unavailableDays = location.unavailable_days || [];
    if (unavailableDates.includes(selectedFmt)) return msg;
    if (unavailableDays.includes(dayOfWeek)) return msg;

    const cutoffTimes = location.cutoff_times || {};
    const todayCutoff = cutoffTimes[todayDayOfWeek];
    let afterCutoff = false;
    if (todayCutoff && typeof todayCutoff === 'string') {
      const [h, m] = todayCutoff.split('.').map(Number);
      if (!Number.isNaN(h) && !Number.isNaN(m)) {
        const cutoff = todayEST.hour(h).minute(m).second(0);
        afterCutoff = todayEST.isAfter(cutoff) || todayEST.isSame(cutoff);
      }
    }

    const sameDay = location.same_day_delivery_pickup;
    if (!sameDay) {
      if (isToday) return msg;
      if (isTomorrow && afterCutoff) return msg;
    } else if (isToday && afterCutoff) {
      return msg;
    }

    return null;
  }

  /* -------------------------------------------------------------- render */

  #syncState() {
    this.dataset.mode = this.staged.mode;
    this.dataset.submode = this.staged.submode;
    // What the non-pickup sub-option returns to — drives its label (delivery vs shipping).
    this.dataset.shipKind = this.#modeForZip(this.staged.zip, this.staged.mode);
  }

  #renderSummary() {
    const c = this.committed;
    const lede = this.querySelector('[data-dw-summary-lede]');
    const primary = this.querySelector('[data-dw-summary-primary]');
    const dateEl = this.querySelector('[data-dw-summary-date]');

    if (c.mode === 'local' && c.submode === 'pickup') {
      const loc = LOCATIONS.find((l) => l.value === c.location);
      if (lede) lede.textContent = 'pickup at ';
      if (primary) primary.textContent = loc ? loc.label : 'a location';
    } else {
      if (lede) lede.textContent = 'delivering to ';
      if (primary) primary.textContent = c.zip || '—';
    }
    if (dateEl && c.date) dateEl.textContent = this.#formatDate(c.date);
  }

  #restoreFromState() {
    const s = this.staged;
    this.#syncState();
    if (this.zipInput) this.zipInput.value = s.zip;
    this.#renderDateLabel();

    // restore toggle thumbs + pressed state
    this.querySelectorAll('[data-dw-toggle]').forEach((track) => {
      const key = track.dataset.dwToggle;
      const options = [...track.querySelectorAll('[data-dw-option]')];
      options.forEach((o, i) => {
        const on = o.dataset.dwOption === s[key];
        o.setAttribute('aria-pressed', String(on));
        if (on) track.querySelector('[data-dw-thumb]')?.classList.toggle('delivery-widget__toggle-thumb--right', i === 1);
      });
    });

    const timeLabel = this.querySelector('[data-dw-time-label]');
    if (timeLabel) timeLabel.textContent = s.time || 'select a time';
    this.#renderLocationLabel();
  }

  // The field label must always mirror staged.location: the ZIP handler clears
  // the location while its field is hidden (delivery mode), and a stale
  // "georgetown" surfacing on the pickup switch reads as a made selection.
  #renderLocationLabel() {
    const locLabel = this.querySelector('[data-dw-location-label]');
    if (!locLabel) return;
    const loc = LOCATIONS.find((l) => l.value === this.staged.location);
    locLabel.textContent = loc ? loc.label : 'select location';
  }

  #renderDateLabel() {
    const el = this.querySelector('[data-dw-date-label]');
    if (!el) return;
    if (this.staged.date) { el.textContent = this.#formatDate(this.staged.date); return; }
    if (this.hasDayjs) el.textContent = 'select a date'; // intentional empty; keep server text if dayjs missing
  }

  #resetTime() {
    this.staged.time = '';
    const timeLabel = this.querySelector('[data-dw-time-label]');
    if (timeLabel) timeLabel.textContent = 'select a time';
  }

  // The slot list depends on mode / sub-mode / location. After one of those
  // changes, keep an already-picked time when the new list still offers it —
  // the customer shouldn't have to re-pick a slot both schedules share.
  #preserveOrResetTime() {
    if (this.staged.time && this.#timeSlots().includes(this.staged.time)) return;
    this.#resetTime();
  }

  /* ------------------------------------------------------------ zip / loc */

  #zipList(attr) {
    const el = document.querySelector(`[${attr}]`);
    return (el?.getAttribute(attr) || '').split(',').map((z) => z.trim()).filter(Boolean);
  }

  #detectLocation(zip) {
    const z = (zip || '').trim();
    if (this.georgetownZips.includes(z)) return 'georgetown';
    if (this.bethesdaZips.includes(z)) return 'bethesda';
    return null;
  }

  #zipType(zip) {
    const z = (zip || '').trim();
    if (!z || this.bethesdaZips.includes(z) || this.georgetownZips.includes(z)) return 'local';
    return 'nationwide';
  }

  #syncLocationFromZip() {
    const loc = this.#detectLocation(this.staged.zip);
    if (loc && loc !== this.staged.location) {
      this.staged.location = loc;
      this.#resetTime();
    } else if (!loc) {
      this.staged.location = '';
    }
    this.#renderLocationLabel();
  }

  /* ------------------------------------------------------------ geolocation */

  // Detection was pending on connect: seed the widget with the detected ZIP,
  // falling back to the block default only once detection has failed. The
  // auto-commit waits for this so the fallback can never reach the cart first.
  async #seedFromGeo() {
    let zip = '';
    try {
      zip = (await detectZip(this.geoConfig)) || '';
    } finally {
      this.#setDetecting(false);
    }
    // The failsafe may have seeded and committed the default already; the
    // once-per-view auto-commit means a late detection could then update the
    // chip without reaching the cart, so exactly one of the two paths seeds.
    if (!this.isConnected || this.geoAbandoned) return;
    if (this.#applyZip(zip || (this.dataset.defaultZip || '').trim())) this.#autoCommit();
  }

  // Loader state of the chip while detection is pending (server renders the
  // same class so there is no flash before this module runs). The chip stays
  // interactive throughout — only the ZIP it is waiting on is masked.
  #setDetecting(on) {
    this.geoPending = on;
    this.classList.toggle('is-detecting', on);
    clearTimeout(this.detectTimer);
    if (on) {
      this.setAttribute('aria-busy', 'true');
      this.detectTimer = setTimeout(() => this.#releaseDetecting(), DETECT_FAILSAFE_MS);
    } else {
      this.removeAttribute('aria-busy');
    }
  }

  // Last resort: a detection promise that never settles must not leave the chip
  // inert forever, so release the loader and fall back to the block default.
  #releaseDetecting() {
    if (!this.geoPending) return;
    this.geoAbandoned = true;
    this.#setDetecting(false);
    if (this.#applyZip((this.dataset.defaultZip || '').trim())) this.#autoCommit();
  }

  // Apply a resolved ZIP unless the customer already committed or edited one.
  #applyZip(zip) {
    if (!/^\d{5}$/.test(zip)) return false;
    if ((this.dataset.committedType || '').trim() !== '') return false;
    // Only a ZIP the customer typed themselves survives detection.
    if (this.userEditedZip || zip === this.staged.zip) return false;

    // Panel open = the customer is mid-edit. The detected ZIP goes into the
    // summary and the baseline "cancel" restores, but never into the staged
    // edit: a ZIP they never typed (and cannot even see in pickup, where the
    // field is hidden) must not drive their mode or reach #apply's setGeoZip.
    if (this.classList.contains('is-open')) {
      this.committed.zip = zip;
      this.committed.mode = this.#modeForZip(zip, this.committed.mode);
      this.#renderSummary();
      return false;
    }

    this.staged.zip = zip;
    if (this.zipInput) this.zipInput.value = zip;
    this.#deriveModeFromZip();
    if (!this.staged.date && this.hasDayjs) this.staged.date = this.#defaultDate(this.staged.mode);
    if (this.staged.mode === 'local' && this.staged.submode === 'delivery') this.#syncLocationFromZip();
    // The reconciled state is the widget's new uncommitted baseline. Without
    // this full sync the chip keeps the pre-detection date and "cancel"
    // restores the pre-detection mode under the detected ZIP.
    this.committed = { ...this.staged };
    this.#restoreFromState();
    this.#renderSummary();
    return true;
  }

  /**
   * Commit an already-orderable pre-filled selection (geo-detected or block
   * defaults) to the cart, once per page view. Without it the purchase gate in
   * blocks/buy-buttons.liquid keeps "Please select a delivery type first" even
   * though the widget shows a complete selection. For local delivery the
   * nearest available time slot is picked automatically; the auto-managed
   * selection is then watched for staleness (see #refreshAutoSelection).
   */
  #autoCommit() {
    if (autoCommitAttempted) return;
    if ((this.dataset.committedType || '').trim() !== '') return;
    if (this.classList.contains('is-open')) return; // customer is mid-edit
    // The seeded date can itself be unorderable — e.g. nationwide defaults to
    // EST-tomorrow, which the cutoff check rejects late in the day — and a
    // silent bail here leaves the whole page gated. Move the selection to the
    // nearest orderable (date, time) before validating.
    if (this.staged.date && this.hasDayjs) {
      const before = { ...this.staged };
      if (!this.#advanceStagedToOrderable()) { this.staged = before; return; }
    }
    if (this.#validate()) return; // incomplete or not orderable — stay manual
    autoCommitAttempted = true;
    this.#markAutoSelection();
    this.committed = { ...this.staged };
    this.#restoreFromState();
    this.#renderSummary();
    this.applyToCart();
  }

  /**
   * Move the STAGED local selection to the nearest orderable (date, time):
   * keep the staged slot when the day still offers it, otherwise take the
   * day's first slot, advancing up to a week when a day is full or past its
   * cutoff. Returns true when staged holds an orderable slot.
   */
  #pickNearestSlot() {
    const s = this.staged;
    if (s.mode !== 'local' || !s.location || !this.hasDayjs) return false;
    const savedDate = s.date;
    const savedTime = s.time;
    const min = this.#minDate('local');
    let day = s.date && !s.date.isBefore(min, 'day') ? s.date : min;
    for (let i = 0; i < 7; i += 1) {
      s.date = day;
      const slots = this.#timeSlots();
      if (slots.length) {
        s.time = savedTime && slots.includes(savedTime) ? savedTime : slots[0];
        if (!this.#checkCutoff()) return true;
      }
      day = day.add(1, 'day');
    }
    s.date = savedDate;
    s.time = savedTime;
    return false;
  }

  /* -------------------------------------------------- auto-selection watch */

  // The auto-managed selection marker survives reloads for the visit, so the
  // watcher keeps guarding it; a manual apply clears it (see #apply) — the
  // widget never silently overrides an explicit customer choice.
  #markAutoSelection() {
    try {
      sessionStorage.setItem('mtn:dw_auto_selection', JSON.stringify({
        mode: this.staged.mode,
        date: this.staged.date ? this.staged.date.format('YYYY-MM-DD') : '',
        time: this.staged.time || '',
      }));
    } catch (e) { /* storage unavailable — staleness watch just won't persist */ }
  }

  #autoSelection() {
    try { return JSON.parse(sessionStorage.getItem('mtn:dw_auto_selection') || 'null'); } catch (e) { return null; }
  }

  // An auto-picked selection can go stale while the customer idles: the slot's
  // start moves past the schedule window, the day passes its cutoff, or
  // midnight invalidates the date. Re-check once a minute and when the tab
  // regains visibility.
  #watchAutoSelection() {
    const { signal } = this.abortController;
    this.staleTimer = setInterval(() => this.#refreshAutoSelection(), 60000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.#refreshAutoSelection();
    }, { signal });
  }

  // Quietly move a stale auto-managed selection to the nearest orderable
  // equivalent, re-apply it, and pulse the chip so the change is noticeable.
  #refreshAutoSelection() {
    if (!this.hasDayjs) return;
    if (this.classList.contains('is-open') || this.classList.contains('is-applying')) return;
    const marker = this.#autoSelection();
    const c = this.committed;
    if (!marker || !c.date) return;
    if (marker.mode !== c.mode || marker.date !== c.date.format('YYYY-MM-DD') || marker.time !== (c.time || '')) return;

    this.staged = { ...c };
    if (!this.#advanceStagedToOrderable()) { this.staged = { ...c }; return; }

    const unchanged = this.staged.date.isSame(c.date, 'day') && (this.staged.time || '') === (c.time || '');
    if (unchanged) { this.staged = { ...c }; return; }
    if (this.#validate()) { this.staged = { ...c }; return; }

    this.#markAutoSelection();
    this.committed = { ...this.staged };
    this.#restoreFromState();
    this.#renderSummary();
    this.#pulseChip();
    this.applyToCart();
  }

  // Move this.staged to the nearest orderable (date, time) for its mode.
  // Returns false when nothing orderable exists within a week — staged is left
  // dirty, the caller restores it.
  #advanceStagedToOrderable() {
    const min = this.#minDate(this.staged.mode);
    if (min && this.staged.date.isBefore(min, 'day')) this.staged.date = min;

    if (this.staged.mode === 'local' && this.staged.submode === 'delivery') {
      const slots = this.#timeSlots();
      const stale = !this.staged.time || !slots.includes(this.staged.time) || Boolean(this.#checkCutoff());
      if (stale && !this.#pickNearestSlot()) return false;
    } else {
      let guard = 0;
      while (this.#checkCutoff() && guard < 7) {
        this.staged.date = this.staged.date.add(1, 'day');
        guard += 1;
      }
      if (guard >= 7) return false;
    }
    return true;
  }

  /**
   * Re-commit a committed selection whose date expired while the customer was
   * away: connectedCallback clamps it for the widget UI only, while every
   * Liquid consumer keeps rendering from the stale cart attributes. Unlike
   * #refreshAutoSelection this also covers explicit customer choices — an
   * expired date cannot be fulfilled — and the result becomes auto-managed so
   * the staleness watcher keeps guarding it.
   */
  #refreshStaleCommitted() {
    if (staleRefreshAttempted || !this.committedDateStale) return;
    if (!this.hasDayjs) return;
    // Mid-edit or mid-apply (applyToCart reconnects this element) — leave it.
    if (this.classList.contains('is-open') || this.classList.contains('is-applying')) return;
    const c = this.committed;
    if (!c.date) return;

    this.staged = { ...c };
    if (!this.#advanceStagedToOrderable()) { this.staged = { ...c }; return; }
    // No "unchanged" skip: an identical selection must still be written — the
    // CART holds the old date, only the in-memory committed one was clamped.
    if (this.#validate()) { this.staged = { ...c }; return; }

    staleRefreshAttempted = true;
    this.#markAutoSelection();
    this.committed = { ...this.staged };
    this.#restoreFromState();
    this.#renderSummary();
    this.#pulseChip();
    this.applyToCart();
  }

  // Draw the eye to the chip when the widget quietly moved a stale selection.
  #pulseChip() {
    this.chip.classList.remove('delivery-widget__field--attention');
    void this.chip.offsetWidth;
    this.chip.classList.add('delivery-widget__field--attention');
    this.chip.addEventListener('animationend', () => this.chip.classList.remove('delivery-widget__field--attention'), { once: true });
  }

  /**
   * The dozen same-day note (blocks/buy-buttons.liquid) links "choose the next
   * available date". Ported from the legacy custom.js flow: move the committed
   * date one day forward, keep the time slot when the next day still offers it,
   * and re-apply; if the selection becomes incomplete, open the panel instead.
   */
  #onChooseNextDate(event) {
    const link = event.target instanceof Element ? event.target.closest('.js-choose-next-date') : null;
    if (!link || !this.hasDayjs) return;
    event.preventDefault();
    event.stopPropagation();

    const next = (this.committed.date || this.#minDate(this.committed.mode)).add(1, 'day');
    if (next.isAfter(this.#today().add(MAX_DAYS_AHEAD, 'day'), 'day')) return;

    this.staged = { ...this.committed, date: next };
    if (this.staged.mode === 'local' && this.staged.time && !this.#timeSlots().includes(this.staged.time)) {
      this.staged.time = ''; // slots differ per day
    }
    this.#restoreFromState();
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (this.#validate()) {
      this.#togglePanel(true); // e.g. time missing — let the customer finish
      return;
    }
    this.committed = { ...this.staged };
    this.#renderSummary();
    this.applyToCart();
  }

  /* ------------------------------------------------------------ time slots */

  #timeSlots() {
    const dayjs = this.#dayjs();
    const data = window.locationData;
    const s = this.staged;
    if (s.mode !== 'local' || !s.date || !s.location || !dayjs || !data || !data[s.location]) return [];

    const location = data[s.location];
    const dayOfWeek = s.date.format('dddd');
    const selectedFmt = s.date.format('YYYY-MM-DD');

    const unavailableDates = location.unavailable_dates || [];
    if (unavailableDates.includes(selectedFmt)) return [];

    const daySchedule = location[`${s.submode}_schedule`]?.[dayOfWeek];
    if (!daySchedule) return [];
    const scheduleParts = daySchedule.split('-');
    if (scheduleParts.length !== 2) return [];

    let workStart = scheduleParts[0].trim();
    const workEnd = (location.cutoff_times?.[dayOfWeek]) || scheduleParts[1].trim();

    let breakStart = null;
    let breakEnd = null;
    const breakSchedule = location[`${s.submode}_cutoff_time`]?.[dayOfWeek];
    if (breakSchedule) {
      const breakParts = breakSchedule.split('-');
      if (breakParts.length === 2) {
        breakStart = this.#toMinutes(breakParts[0].trim());
        breakEnd = this.#toMinutes(breakParts[1].trim());
      }
    }

    workStart = this.#adjustStartForToday(workStart, s.date);

    const startMinutes = this.#toMinutes(workStart);
    const endMinutes = this.#toMinutes(workEnd);
    const slotDuration = 60;
    const slots = [];
    let current = startMinutes;

    while (current + slotDuration <= endMinutes) {
      const slotStart = current;
      const slotEnd = current + slotDuration;
      if (this.#inBreak(slotStart, slotEnd, breakStart, breakEnd)) {
        current = slotStart < breakEnd ? breakEnd : current + slotDuration;
        continue;
      }
      slots.push(`${this.#toTimeString(slotStart)} - ${this.#toTimeString(slotEnd)}`);
      current += slotDuration;
    }

    return slots;
  }

  #toMinutes(timeStr) {
    const [h, m] = (timeStr || '').split('.').map(Number);
    return (h || 0) * 60 + (m || 0);
  }

  #toTimeString(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
    return `${displayHours}:${mins.toString().padStart(2, '0')}${period}`;
  }

  #adjustStartForToday(workStart, date) {
    const todayEST = this.#nowEST();
    if (date.format('YYYY-MM-DD') !== todayEST.format('YYYY-MM-DD')) return workStart;

    const oneHourLater = todayEST.add(1, 'hour');
    const minutes = oneHourLater.minute();
    let adjustedHours = oneHourLater.hour();
    let roundedMinutes;
    if (minutes === 0) {
      roundedMinutes = 0;
    } else if (minutes <= 30) {
      roundedMinutes = 30;
    } else {
      roundedMinutes = 0;
      adjustedHours += 1;
    }

    const minStart = `${adjustedHours}.${String(roundedMinutes).padStart(2, '0')}`;
    return this.#toMinutes(minStart) > this.#toMinutes(workStart) ? minStart : workStart;
  }

  #inBreak(slotStart, slotEnd, breakStart, breakEnd) {
    if (!breakStart || !breakEnd) return false;
    return (slotStart >= breakStart && slotStart < breakEnd) ||
      (slotEnd > breakStart && slotEnd <= breakEnd) ||
      (slotStart < breakStart && slotEnd > breakEnd);
  }

  /* ---------------------------------------------------------- date helpers */

  #dayjs() { return window.dayjs; }

  #nowEST() {
    const d = this.#dayjs();
    if (!d) return null;
    return d.tz ? d().tz('America/New_York') : d();
  }

  #today() {
    const d = this.#dayjs();
    return d ? (d.tz ? d().tz('America/New_York').startOf('day') : d().startOf('day')) : null;
  }

  #minDate(mode) {
    const t = this.#today();
    if (!t) return null;
    return mode === 'nationwide' ? t.add(1, 'day') : t;
  }

  #defaultDate(mode) { return this.#minDate(mode); }

  #formatDate(d) {
    if (!d) return '';
    return d.format('D MMMM').toLowerCase(); // "10 july"
  }

  #storageDate(d) {
    if (!d) return '';
    return d.format('DD MMMM YYYY'); // "10 July 2026" — matches the legacy cart attribute
  }

  #currentDeliveryDate(d) {
    if (!d) return '';
    return `${d.format('dddd')}|${d.format('MMMM')}`; // "Friday|July"
  }
}

if (!customElements.get('delivery-widget')) {
  customElements.define('delivery-widget', DeliveryWidget);
}
